import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import invoiceAPI from '../../api/invoiceAPI';
import customerAPI from '../../api/customerAPI';
import productAPI from '../../api/productAPI';
import chartOfAccountsAPI from '../../api/chartOfAccountsAPI';

const NewInvoicePage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    
    // Data states
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [accounts, setAccounts] = useState([]);
    
    // Form state
    const [formData, setFormData] = useState({
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        customer: '',
        terms: 'net_30',
        customTerms: '',
        memo: '',
        lines: [
            { product: '', description: '', quantity: 1, unitPrice: 0, discount: 0, taxRate: 0, lineTotal: 0 }
        ]
    });

    // Search states
    const [customerSearch, setCustomerSearch] = useState('');
    const [productSearch, setProductSearch] = useState('');

    // New customer modal state
    const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
    const [newCustomerData, setNewCustomerData] = useState({
        name: '',
        email: '',
        phone: '',
        promoOptIn: {
            email: false,
            sms: false
        }
    });
    const [creatingCustomer, setCreatingCustomer] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    // Calculate due date based on terms
    useEffect(() => {
        if (formData.terms && formData.invoiceDate) {
            const invoiceDate = new Date(formData.invoiceDate);
            let dueDate = new Date(invoiceDate);
            
            switch (formData.terms) {
                case 'due_on_receipt':
                    dueDate = invoiceDate;
                    break;
                case 'net_15':
                    dueDate.setDate(invoiceDate.getDate() + 15);
                    break;
                case 'net_30':
                    dueDate.setDate(invoiceDate.getDate() + 30);
                    break;
                case 'net_60':
                    dueDate.setDate(invoiceDate.getDate() + 60);
                    break;
                case '2_10_net_30':
                    dueDate.setDate(invoiceDate.getDate() + 30);
                    break;
                default:
                    dueDate.setDate(invoiceDate.getDate() + 30);
            }
            
            setFormData(prev => ({
                ...prev,
                dueDate: dueDate.toISOString().split('T')[0]
            }));
        }
    }, [formData.terms, formData.invoiceDate]);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const [customersRes, productsRes, accountsRes] = await Promise.all([
                customerAPI.getAll(),
                productAPI.getAll(),
                chartOfAccountsAPI.getAll()
            ]);

            setCustomers(customersRes.data || []);
            setProducts(productsRes.data || []);
            setAccounts(accountsRes.data || []);
        } catch (err) {
            setError('Failed to load initial data: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredCustomers = customers.filter(customer =>
        customer.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
        customer.email?.toLowerCase().includes(customerSearch.toLowerCase()) ||
        customer.companyName?.toLowerCase().includes(customerSearch.toLowerCase())
    );

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        product.sku?.toLowerCase().includes(productSearch.toLowerCase()) ||
        product.description?.toLowerCase().includes(productSearch.toLowerCase())
    );

    // Add New Customer Function
    const handleCreateCustomer = async () => {
        if (!newCustomerData.name.trim()) {
            setError('Customer name is required');
            return;
        }
    
        setCreatingCustomer(true);
        try {
            //console.log('Creating customer with data:', newCustomerData);
            const response = await customerAPI.create(newCustomerData);
            //console.log('Full API response:', response);
            
            // Handle different response structures
            let newCustomer;
            if (response.data) {
                newCustomer = response.data;
            } else if (response._id) {
                // If the response is the customer object directly
                newCustomer = response;
            } else {
                throw new Error('Unexpected response format from server');
            }
            
            if (!newCustomer || !newCustomer._id) {
                console.error('Invalid customer data:', newCustomer);
                throw new Error('Invalid customer data received from server');
            }
    
            // Ensure the customer has required properties
            const safeCustomer = {
                _id: newCustomer._id,
                name: newCustomer.name || newCustomerData.name,
                email: newCustomer.email || newCustomerData.email,
                phone: newCustomer.phone || newCustomerData.phone,
                promoOptIn: newCustomer.promoOptIn || newCustomerData.promoOptIn,
                // Add any other properties that might be needed
                ...newCustomer
            };
    
            console.log('Safe customer data for state:', safeCustomer);
            
            // Add the new customer to the local state
            setCustomers(prev => [safeCustomer, ...prev]);
            
            // Auto-select the new customer
            handleCustomerSelect(safeCustomer._id);
            
            // Close the modal and reset form
            setShowNewCustomerModal(false);
            setNewCustomerData({
                name: '',
                email: '',
                phone: '',
                promoOptIn: {
                    email: false,
                    sms: false
                }
            });
            
            setSuccess('Customer created successfully');
        } catch (err) {
            console.error('Error creating customer:', err);
            setError(err.message || 'Failed to create customer');
        } finally {
            setCreatingCustomer(false);
        }
    };

    const handleCustomerSelect = (customerId) => {
        const customer = customers.find(c => c._id === customerId);
        if (customer) {
            setFormData(prev => ({
                ...prev,
                customer: customerId,
                customerName: customer.name,
                customerEmail: customer.email,
                customerAddress: customer.billingAddress
            }));
            setCustomerSearch(customer.name); // Show selected customer name in search
        }
    };

    const addLine = () => {
        setFormData({
            ...formData,
            lines: [
                ...formData.lines,
                { product: '', description: '', quantity: 1, unitPrice: 0, discount: 0, taxRate: 0, lineTotal: 0 }
            ]
        });
    };

    const removeLine = (index) => {
        if (formData.lines.length <= 1) return;
        const newLines = formData.lines.filter((_, i) => i !== index);
        setFormData({ ...formData, lines: newLines });
    };

    const updateLine = (index, field, value) => {
        const newLines = [...formData.lines];
        
        // Handle different field types
        if (field === 'quantity' || field === 'unitPrice' || field === 'discount' || field === 'taxRate') {
            newLines[index][field] = parseFloat(value) || 0;
        } else {
            newLines[index][field] = value;
        }
    
        // Always recalculate line total when any field changes
        const quantity = parseFloat(newLines[index].quantity) || 0;
        const unitPrice = parseFloat(newLines[index].unitPrice) || 0;
        const discount = parseFloat(newLines[index].discount) || 0;
        
        const subtotal = quantity * unitPrice;
        const discountAmount = (subtotal * discount) / 100;
        newLines[index].lineTotal = parseFloat((subtotal - discountAmount).toFixed(2));
    
        setFormData({ ...formData, lines: newLines });
    };

    const calculateTotals = () => {
        const subtotal = formData.lines.reduce((sum, line) => sum + ((parseFloat(line.lineTotal) || 0)  / 1.12), 0);
        const discountTotal = formData.lines.reduce((sum, line) => {
            const lineSubtotal = (parseFloat(line.quantity) || 0) * (parseFloat(line.unitPrice) || 0);
            return sum + ((lineSubtotal * (parseFloat(line.discount) || 0)) / 100);
        }, 0);
        const taxTotal = formData.lines.reduce((sum, line) => {
            const taxableAmount = (parseFloat(line.lineTotal) || 0) / 1.12;
            return sum + ((taxableAmount * (parseFloat(line.taxRate) || 0)) / 100);
        }, 0);
        const totalAmount = subtotal + taxTotal;
    
        return { 
            subtotal: parseFloat(subtotal.toFixed(2)), 
            discountTotal: parseFloat(discountTotal.toFixed(2)), 
            taxTotal: parseFloat(taxTotal.toFixed(2)), 
            totalAmount: parseFloat(totalAmount.toFixed(2)) 
        };
    };

    const handleSubmit = async (action) => {
        const { subtotal, discountTotal, taxTotal, totalAmount } = calculateTotals();
        
        if (!formData.customer) {
            setError('Customer is required');
            return;
        }
    
        if (formData.lines.some(line => !line.product || line.quantity <= 0)) {
            setError('All lines must have a product and quantity greater than 0');
            return;
        }
    
        if (totalAmount <= 0) {
            setError('Invoice total must be greater than 0');
            return;
        }
    
        try {
            setLoading(true);
            setError('');
            setSuccess('');
    
            // Prepare the payload with all required fields as numbers
            const payload = {
                invoiceDate: formData.invoiceDate,
                dueDate: formData.dueDate,
                customer: formData.customer,
                terms: formData.terms,
                memo: formData.memo || '',
                lines: formData.lines.map(line => ({
                    product: line.product,
                    description: line.description || '',
                    quantity: Number(line.quantity) || 0,
                    unitPrice: Number(line.unitPrice) || 0,
                    discount: Number(line.discount) || 0,
                    taxRate: Number(line.taxRate) || 0,
                    lineTotal: Number(line.lineTotal) || 0
                })),
                // Include all required total fields as explicit numbers
                subtotal: Number(subtotal),
                discountTotal: Number(discountTotal),
                taxTotal: Number(taxTotal),
                totalAmount: Number(totalAmount),
                amountDue: Number(totalAmount)
            };
    
            // Only include customTerms if terms is 'custom' and it has a value
            if (formData.terms === 'custom' && formData.customTerms) {
                payload.customTerms = formData.customTerms;
            }
    
            console.log('Creating invoice with payload:', payload);
            console.log('Payload types:', {
                subtotal: typeof payload.subtotal,
                discountTotal: typeof payload.discountTotal,
                taxTotal: typeof payload.taxTotal,
                totalAmount: typeof payload.totalAmount,
                amountDue: typeof payload.amountDue
            });
    
            if (action === 'saveAsDraft') {
                const response = await invoiceAPI.create(payload);
                setSuccess('Invoice saved as draft successfully');
                setTimeout(() => {
                    navigate(`/invoice/${response.data._id}`);
                }, 1500);
            } else if (action === 'saveAndSend') {
                const response = await invoiceAPI.create(payload);
                await invoiceAPI.postToAccounting(response.data._id);
                setSuccess('Invoice created and posted to accounting successfully');
                setTimeout(() => {
                    navigate(`/invoice/${response.data._id}`);
                }, 1500);
            }
        } catch (err) {
            console.error('Error creating invoice:', err);
            // Show more detailed error information
            if (err.response?.data?.errors) {
                const errorMessages = Object.values(err.response.data.errors).map(error => error.message).join(', ');
                setError(`Validation failed: ${errorMessages}`);
            } else {
                setError(err.message || 'Failed to create invoice');
            }
        } finally {
            setLoading(false);
        }
    };

    const { subtotal, discountTotal, taxTotal, totalAmount } = calculateTotals();

    const termsOptions = [
        { value: 'due_on_receipt', label: 'Due on Receipt' },
        { value: 'net_15', label: 'Net 15' },
        { value: 'net_30', label: 'Net 30' },
        { value: 'net_60', label: 'Net 60' },
        { value: '2_10_net_30', label: '2/10 Net 30' },
        { value: 'custom', label: 'Custom' }
    ];

    return (
        <Layout pageTitle="Create Invoice">
            <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Create Invoice</h1>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <span>Auto-post to: Accounts Receivable, Sales, COGS, Inventory</span>
                    </div>
                </div>
                
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                        {success}
                    </div>
                )}

                {/* Customer Section */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4">Customer Information</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Customer *
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search customers or add new..."
                                    value={customerSearch}
                                    onChange={(e) => setCustomerSearch(e.target.value)}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                />
                                {/* In the customer search dropdown */}
                                {customerSearch && filteredCustomers.length > 0 && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-60 overflow-y-auto">
                                        {filteredCustomers.map(customer => (
                                            <div
                                                key={customer._id}
                                                onClick={() => handleCustomerSelect(customer._id)}
                                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b"
                                            >
                                                <div className="font-medium">{customer.name || 'Unnamed Customer'}</div>
                                                {customer.companyName && (
                                                    <div className="text-sm text-gray-600">{customer.companyName}</div>
                                                )}
                                                {customer.email && (
                                                    <div className="text-sm text-gray-500">{customer.email}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {/* Add New Customer Option */}
                                {customerSearch && !filteredCustomers.some(c => 
                                    c.name.toLowerCase() === customerSearch.toLowerCase() ||
                                    c.email?.toLowerCase() === customerSearch.toLowerCase()
                                ) && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg">
                                        <div
                                            onClick={() => setShowNewCustomerModal(true)}
                                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b bg-teal-50"
                                        >
                                            <div className="font-medium text-teal-700">
                                                + Add "{customerSearch}" as new customer
                                            </div>
                                            <div className="text-sm text-teal-600">
                                                Create new customer profile
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {formData.customer && (
                                <div className="mt-2 p-2 bg-gray-50 rounded">
                                    <div className="font-medium">
                                        {customers.find(c => c._id === formData.customer)?.name}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {customers.find(c => c._id === formData.customer)?.email}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Customer Email
                            </label>
                            <input
                                type="email"
                                value={formData.customerEmail || ''}
                                onChange={(e) => setFormData({...formData, customerEmail: e.target.value})}
                                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                placeholder="customer@example.com"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Invoice Date *
                            </label>
                            <input
                                type="date"
                                value={formData.invoiceDate}
                                onChange={(e) => setFormData({...formData, invoiceDate: e.target.value})}
                                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Due Date *
                            </label>
                            <input
                                type="date"
                                value={formData.dueDate}
                                onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Payment Terms
                            </label>
                            <select
                                value={formData.terms}
                                onChange={(e) => setFormData({...formData, terms: e.target.value})}
                                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                            >
                                {termsOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {formData.terms === 'custom' && (
                                <input
                                    type="text"
                                    value={formData.customTerms}
                                    onChange={(e) => setFormData({...formData, customTerms: e.target.value})}
                                    className="w-full border rounded px-3 py-2 mt-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    placeholder="Enter custom terms"
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Rest of the component remains the same... */}
                {/* Line Items Section */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">Line Items</h2>
                        <button
                            type="button"
                            onClick={addLine}
                            className="px-3 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 flex items-center text-sm"
                        >
                            <span className="mr-1">+</span> Add Item
                        </button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Product/Service *
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Description
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Qty
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Rate
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Discount %
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tax %
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Amount
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {formData.lines.map((line, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="relative">
                                                <select
                                                    value={line.product}
                                                    onChange={(e) => updateLine(index, 'product', e.target.value)}
                                                    className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                                    required
                                                >
                                                    <option value="">Select Product</option>
                                                    {filteredProducts.map(product => (
                                                        <option key={product._id} value={product._id}>
                                                            {product.name} - ₱{product.salePrice}
                                                            {product.trackInventory && ` (Stock: ${product.quantityOnHand})`}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <input
                                                type="text"
                                                value={line.description}
                                                onChange={(e) => updateLine(index, 'description', e.target.value)}
                                                className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                                placeholder="Item description"
                                            />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                value={line.quantity}
                                                onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                                                className="w-20 border rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-teal-500"
                                            />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={line.unitPrice}
                                                onChange={(e) => updateLine(index, 'unitPrice', e.target.value)}
                                                className="w-24 border rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-teal-500"
                                            />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                value={line.discount}
                                                onChange={(e) => updateLine(index, 'discount', e.target.value)}
                                                className="w-20 border rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-teal-500"
                                            />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                value={line.taxRate}
                                                onChange={(e) => updateLine(index, 'taxRate', e.target.value)}
                                                className="w-20 border rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-teal-500"
                                            />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-right font-medium">
                                            ₱{line.lineTotal.toFixed(2)}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-center">
                                            <button
                                                type="button"
                                                onClick={() => removeLine(index)}
                                                disabled={formData.lines.length <= 1}
                                                className="text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed text-sm"
                                            >
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Totals Summary */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-end">
                            <div className="w-64 space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Subtotal:</span>
                                    <span className="font-medium">₱{subtotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Discount:</span>
                                    <span className="font-medium text-red-600">-₱{discountTotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Tax:</span>
                                    <span className="font-medium">₱{taxTotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-t pt-2">
                                    <span className="text-lg font-bold">Total:</span>
                                    <span className="text-lg font-bold">₱{totalAmount.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Memo Field */}
                    <div className="mt-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                            Memo / Notes
                        </label>
                        <textarea
                            value={formData.memo}
                            onChange={(e) => setFormData({...formData, memo: e.target.value})}
                            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                            rows={3}
                            placeholder="Additional notes or terms for the customer..."
                        />
                    </div>
                </div>

                {/* Accounting Preview */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-blue-800 mb-3">Accounting Preview</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="font-medium text-blue-700 mb-2">Sales Entry (Auto-generated):</h4>
                            <div className="text-sm space-y-1">
                                <div className="flex justify-between">
                                    <span>Dr Accounts Receivable</span>
                                    <span className="font-medium">₱{totalAmount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Cr Sales Revenue</span>
                                    <span className="font-medium">₱{(totalAmount - taxTotal).toFixed(2)}</span>
                                </div>
                                {taxTotal > 0 && (
                                    <div className="flex justify-between">
                                        <span>Cr Sales Tax Payable</span>
                                        <span className="font-medium">₱{taxTotal.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div>
                            <h4 className="font-medium text-blue-700 mb-2">COGS Entry (Auto-generated for inventory items):</h4>
                            <div className="text-sm space-y-1 text-gray-600">
                                <div>Dr Cost of Goods Sold</div>
                                <div>Cr Inventory</div>
                                <div className="text-xs italic">Calculated based on product cost</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4">
                    <button
                        type="button"
                        onClick={() => navigate('/invoice')}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSubmit('saveAsDraft')}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save as Draft'}
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSubmit('saveAndSend')}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                        disabled={loading}
                    >
                        {loading ? 'Creating...' : 'Save and Send Invoice'}
                    </button>
                </div>

                {/* New Customer Modal */}
                {showNewCustomerModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                            <div className="px-6 py-4 border-b">
                                <h3 className="text-lg font-semibold">Add New Customer</h3>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Customer Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={newCustomerData.name}
                                        onChange={(e) => setNewCustomerData({
                                            ...newCustomerData,
                                            name: e.target.value
                                        })}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="Enter customer name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        value={newCustomerData.email}
                                        onChange={(e) => setNewCustomerData({
                                            ...newCustomerData,
                                            email: e.target.value
                                        })}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="customer@example.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        value={newCustomerData.phone}
                                        onChange={(e) => setNewCustomerData({
                                            ...newCustomerData,
                                            phone: e.target.value
                                        })}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="+1 (555) 123-4567"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Promotional Preferences
                                    </label>
                                    <div className="flex items-center space-x-4">
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={newCustomerData.promoOptIn.email}
                                                onChange={(e) => setNewCustomerData({
                                                    ...newCustomerData,
                                                    promoOptIn: {
                                                        ...newCustomerData.promoOptIn,
                                                        email: e.target.checked
                                                    }
                                                })}
                                                className="mr-2"
                                            />
                                            <span className="text-sm">Email</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={newCustomerData.promoOptIn.sms}
                                                onChange={(e) => setNewCustomerData({
                                                    ...newCustomerData,
                                                    promoOptIn: {
                                                        ...newCustomerData.promoOptIn,
                                                        sms: e.target.checked
                                                    }
                                                })}
                                                className="mr-2"
                                            />
                                            <span className="text-sm">SMS</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowNewCustomerModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    disabled={creatingCustomer}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCreateCustomer}
                                    className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm font-medium"
                                    disabled={creatingCustomer}
                                >
                                    {creatingCustomer ? 'Creating...' : 'Create Customer'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default NewInvoicePage;